/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match */

import { Component, Input, ViewChild, Output, EventEmitter, OnDestroy } from '@angular/core';
import {
  SchemaField,
  Document,
  DateFieldFormat,
  DateFieldDefault,
  DocumentSchema,
  SystemFieldType
} from 'src/app/modules/core/store/models/document.model';
import { DocumentReader } from 'src/app/modules/core/store/models/document-reader';
import { DynamicField } from '../dynamic-field';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ViewComponent } from '../../../store/models/workflow.model';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { CultureService } from '../../../services/culture.service';

@Component({
    selector: 'app-date-field',
    templateUrl: './date-field.component.html',
    styleUrls: ['./date-field.component.scss'],
    standalone: false
})
export class DateFieldComponent implements DynamicField, OnDestroy {
  @Output() valueChanged = new EventEmitter<DynamicField>();
  @ViewChild('picker') picker: any;
  @Input() field: SchemaField;
  @Input() displayName: string;
  @Input() layerUId: string;
  @Input() set document(value: Document) {
    this.reader = new DocumentReader(value);
  }
  reader: DocumentReader;

  @Input() viewComponent: ViewComponent;
  @Input() schema: DocumentSchema;
  @Input() dataset: Dataset;

  DateFieldFormat = DateFieldFormat;
  DateFieldDefault = DateFieldDefault;
  timeFormat = 12;
  momentValue: moment.Moment;
  isReadOnly: boolean;
  showFieldAsEdit: boolean = false;

  private _isEdit: boolean = false;
  @Input() set isEdit(edit: boolean) {
    this._isEdit = edit;

    this.calculateShouldDisplayFieldForEdit();
  }
  get isEdit(): boolean {
    return this._isEdit;
  }

  calculateShouldDisplayFieldForEdit() {
    this.showFieldAsEdit = this._isEdit && !this.isReadOnly;
  }

  editorConfig = {
    placeholder: ''
  };

  get dateValue(): moment.Moment {
    return this.momentValue;
  }
  set dateValue(val: moment.Moment | string) {
    // value being returned from date picker is an empty string when cleared
    if (typeof val === 'string') {
      this.momentValue = null;
    } else {
      if (!this.momentValue) {
        this.momentValue = moment();
      }

      if (!!val && !moment.isMoment(val)) {
        val = moment(val);
      }

      this.momentValue.date(val.date());
      this.momentValue.month(val.month());
      this.momentValue.year(val.year());

      if (this.showTime) {
        this.momentValue.hour(val.hour());
        this.momentValue.minute(val.minute());
        this.momentValue.seconds(0);
      }
    }
  }

  get timeValue(): string {
    if (!this.momentValue) {
      return null;
    }

    return this.momentValue.format('h:mm A');
  }

  get value(): any {
    return this.momentValue ? this.momentValue.toISOString() : null;
  }

  get showDate(): boolean {
    return (
      this.field?.settings?.format === DateFieldFormat.DateOnly ||
      this.field?.settings?.format === DateFieldFormat.LongDateWithTime ||
      this.field?.settings?.format === DateFieldFormat.LongDateWithTime24Hr
    );
  }

  get showTime(): boolean {
    return this.field.settings.format !== DateFieldFormat.DateOnly;
  }

  get formattedFieldValue(): string {
    const val = this.reader?.getFormattedFieldValue(this.field?.systemName, this.field, this.cultureService);
    return val || '---';
  }

  constructor(private translate: TranslateService, private cultureService: CultureService) {}

  ngOnDestroy(): void {
    this.valueChanged.complete();
  }

  tpOnContentUpdated(): void {
    this.timeFormat =
      this.field?.settings?.format === DateFieldFormat.LongDateWithTime24Hr || this.field.settings.format === DateFieldFormat.TimeOnly24Hr
        ? 24
        : 12;

    const value = this.reader.getFieldValue(this.field.systemName);
    this.momentValue = value ? moment(value) : null;

    if ((this.field.fieldType & SystemFieldType.Editable) !== SystemFieldType.Editable || this.viewComponent?.configuration?.isReadOnly) {
      this.isReadOnly = true;
      this.calculateShouldDisplayFieldForEdit();
    }
  }

  onValueChanged(): void {
    this.valueChanged.emit(this);
  }

  onTimeChanged(time: string): void {
    const colon = time.indexOf(':');

    let hour = parseInt(time.substring(0, colon), 10);
    const minute = parseInt(time.substring(colon + 1, colon + 3), 10);

    if (time.indexOf('PM') >= 0) {
      if (hour < 12) {
        hour += 12;
      }
    } else if (time.indexOf('AM') >= 0) {
      if (hour >= 12) {
        hour -= 12;
      }
    }

    if (!this.momentValue) {
      this.momentValue = moment();
    }

    this.momentValue.hour(hour);
    this.momentValue.minute(minute);
    this.momentValue.seconds(0);

    this.onValueChanged();
  }
}
